import { useCallback, useEffect, useMemo, useState } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { wafFetch } from '@v2/shared/lib/wafFetch';
import { Magazine, MagazinePage } from './types';

const ENDPOINT = '/services_api/storefront/v1/third_party/MagzterGold/products.json';

/** Long enough that typing a word is one request rather than five. */
const SEARCH_DEBOUNCE_MS = 400;

/** What the visitor has asked for. One object, so a change to it is always atomic. */
type Request = {
  categoryId: string | null;
  search: string;
  page: number;
};

/** The request minus its page — what makes one list a different list from another. */
function listKey(request: Request): string {
  return `${request.categoryId ?? ''}|${request.search}`;
}

function buildUrl(base: string, request: Request): string {
  const params = new URLSearchParams({ page: String(request.page) });

  // `!= null`, loosely, which is the one place in here that is deliberate: it excludes
  // both null and undefined while keeping "0" and "", because these ids are the third
  // party's and are never interpreted — an id of "0" is a category like any other, and
  // truthiness would drop it and quietly answer with the unfiltered list instead.
  //
  // undefined is not hypothetical. A serializer that omits null fields hands the
  // component a category with no `id` key at all, and `String(undefined)` in a query
  // string is the literal "undefined" — a filter matching nothing, on the entry every
  // visitor lands on. Fixed at the source too (see the props builder), belt and braces.
  // eslint-disable-next-line eqeqeq
  if (request.categoryId != null) {
    params.set('category', request.categoryId);
  }

  if (request.search !== '') {
    params.set('search_term', request.search);
  }

  return `${base}?${params.toString()}`;
}

type Loaded = {
  key: string;
  /** By page number, one-based minus one — not appended, so a repeat replaces. */
  pages: Magazine[][];
  total: number | null;
  limit: number | null;
  /** The highest page that came back, and whether it had anything on it. */
  lastPage: number;
  lastPageWasEmpty: boolean;
};

/**
 * The magazines, and every way the visitor has of changing which ones.
 *
 * All of it happens in the browser, as it did at the source: the sidebar and the heading
 * are the server's, the grid is not. What the server does render is the page a search
 * engine sees, and there is nothing here worth indexing — 14,000 covers across 480-odd
 * pages, none of which we sell.
 *
 * Category, search and page live in ONE piece of state rather than three. That is not
 * tidiness: picking a category has to reset the page, and with the page held separately
 * the reset lands a render later than the fetch does — so switching category while on
 * page 3 fires a request for page 3 of the new category and then aborts it. Changing
 * them together makes that unrepresentable.
 *
 * Two things differ from headless_jshop's version, both because the endpoint now answers
 * with the storefront envelope rather than a bare array:
 *
 * - It knows when to stop. headless could only end by asking for one page too many and
 *   getting nothing back; the envelope reports how many pages there are, so the last
 *   page is normally the last request. See `hasMore` for why the empty-page test is
 *   still kept as a backstop.
 * - It cannot double up. headless appended every page and then deduplicated by object
 *   identity, which never removes anything — two requests in flight over the same page
 *   rendered the same covers twice. Pages are held by number here, so a repeat replaces.
 */
export default function useMagazines() {
  const { buildUrl: absolute } = useUrlGenerator();
  const endpoint = absolute(ENDPOINT);

  const [request, setRequest] = useState<Request>({ categoryId: null, search: '', page: 1 });
  // What the box says, which leads `request.search` by the debounce.
  const [searchInput, setSearchInput] = useState('');

  const [loaded, setLoaded] = useState<Loaded>({
    key: listKey({ categoryId: null, search: '', page: 1 }),
    pages: [],
    total: null,
    limit: null,
    lastPage: 0,
    lastPageWasEmpty: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setRequest((current) =>
        current.search === searchInput ? current : { ...current, search: searchInput, page: 1 }
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const key = listKey(request);
    const controller = new AbortController();

    setIsLoading(true);
    setHasFailed(false);

    wafFetch(buildUrl(endpoint, request), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Magazines answered ${response.status}`);
        }

        return response.json() as Promise<MagazinePage>;
      })
      .then((answer) => {
        setLoaded((current) => {
          // A different list than the one in state means this is the first page of a new
          // one — start it empty rather than merging into the previous category's pages.
          const base = current.key === key ? current.pages.slice() : [];
          const results = answer.results ?? [];
          base[request.page - 1] = results;

          return {
            key,
            pages: base,
            total: answer.total ?? 0,
            limit: answer.limit || null,
            lastPage: request.page,
            lastPageWasEmpty: results.length === 0,
          };
        });
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        // An abort is this effect being superseded, not a failure — the newer request
        // owns the state now, and saying "we could not load these" over it would be a
        // lie. It is also what keeps a stale answer out of the merge above: a superseded
        // request rejects here rather than resolving there.
        if (controller.signal.aborted) {
          return;
        }

        console.error('Failed to load Magzter Gold magazines', error);
        setHasFailed(true);
        setIsLoading(false);
      });

    return () => controller.abort();
    // `endpoint` is derived from the site, which does not change within a page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Only the list the visitor is currently asking for. Anything still in state under a
  // different key belongs to the category they just left, and showing it under the new
  // heading for the length of one request reads as the wrong covers rather than as
  // loading.
  const isCurrent = loaded.key === listKey(request);

  // Memoised because it is not cheap and it runs on every render — and one of those
  // renders happens on every keystroke in the search box, 400ms before the debounce
  // does anything, over however many hundred covers are already loaded.
  const allPages = useMemo(
    () => loaded.pages.reduce<Magazine[]>((all, one) => all.concat(one ?? []), []),
    [loaded.pages]
  );

  const magazines = isCurrent ? allPages : [];

  return {
    magazines,
    isLoading,
    hasFailed,
    /**
     * Counted in PAGES, not in covers, and that is the whole point.
     *
     * `magazines.length < total` looks equivalent and is not: `total` is the third
     * party's own count of matching titles, while `results` is what survived
     * normalising — a title with no cover or no name is dropped. One of those anywhere
     * in the catalogue means the accumulated length can never reach `total`, so
     * "there is more" would stay true after the last page, the sentinel would sit at
     * the bottom of a grid that has stopped growing, and it would ask for page 483,
     * 484, 485 forever — each one an uncached round trip to India. The same happens if
     * the third party caps how deep it will page while still reporting the full count.
     *
     * The empty-page test is the backstop for both: whatever the arithmetic says, a
     * page that came back with nothing on it is the end.
     */
    hasMore:
      isCurrent &&
      !hasFailed &&
      !loaded.lastPageWasEmpty &&
      loaded.total !== null &&
      loaded.lastPage < Math.ceil(loaded.total / (loaded.limit || 1)),
    loadMore: useCallback(() => {
      setRequest((current) => ({ ...current, page: current.page + 1 }));
    }, []),
    searchInput,
    setSearchInput,
    selectedCategoryId: request.categoryId,
    selectCategory: useCallback((categoryId: string | null) => {
      // Clearing the box with the category is what the sidebar did at the source: a term
      // typed against one category rarely means anything against the next, and leaving it
      // in place reads as a category that has almost nothing in it.
      setSearchInput('');
      setRequest({ categoryId, search: '', page: 1 });
    }, []),
  };
}
